import React, { useEffect, useMemo, useState } from "react";

type MentorCategory =
  | "All"
  | "Foundation"
  | "JEE"
  | "NEET"
  | "Boards"
  | "Doubt Experts";

type Mentor = {
  id: number;
  name: string;
  role: string;
  seoTitle: string;
  category: Exclude<MentorCategory, "All">;
  subject: string;
  experience: string;
  students: string;
  rating: string;
  image: string;
  imageAlt: string;
  bio: string;
  skills: string[];
  keywords: string;
};

type MentorStat = {
  value: string;
  label: string;
  keywords: string;
};

type SupportItem = {
  title: string;
  desc: string;
  keywords: string;
};

type TeachingFlow = {
  step: string;
  title: string;
  desc: string;
  keywords: string;
};

const categories: MentorCategory[] = [
  "All",
  "Foundation",
  "JEE",
  "NEET",
  "Boards",
  "Doubt Experts",
];

const categorySeoText: Record<MentorCategory, string> = {
  All: "Expert teachers and mentors for Foundation, CBSE boards, JEE Main, NEET UG, doubt solving, answer writing, revision planning and performance improvement.",
  Foundation:
    "Foundation mentors for Class 7, Class 8, Class 9 and Class 10 students with Maths, Science, reasoning, concept clarity and school exam preparation.",
  JEE: "JEE mentors for Physics, Chemistry and Mathematics with JEE Main preparation, JEE Advanced problem solving, numerical practice and PYQ strategy.",
  NEET: "NEET mentors for Physics, Chemistry and Biology with NCERT revision, Biology diagrams, Chemistry reactions, MCQ practice and medical entrance preparation.",
  Boards:
    "Board exam mentors for CBSE, NCERT, Class 10 and Class 12 students with answer writing, sample papers, grammar, revision planning and presentation skills.",
  "Doubt Experts":
    "Live doubt solving experts for Maths, Science, Physics, Chemistry, Biology, JEE, NEET and board exam questions with step-by-step explanations.",
};

const mentors: Mentor[] = [
  {
    id: 1,
    name: "Dr. Ananya Sharma",
    role: "Senior Biology Mentor",
    seoTitle: "NEET Biology Mentor for NCERT Revision Diagrams and MCQ Practice",
    category: "NEET",
    subject: "Biology",
    experience: "9+ Years",
    students: "18k+",
    rating: "4.9",
    image:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=900&h=900&fit=crop&auto=format&q=80",
    imageAlt:
      "NEET Biology mentor profile for NCERT revision diagrams and medical entrance preparation",
    bio: "Specializes in NCERT-focused Biology preparation with diagram-based explanations, concept clarity, chapter revision and exam-oriented MCQ practice for NEET UG students.",
    skills: ["NCERT Biology", "NEET MCQs", "Diagram Practice"],
    keywords:
      "NEET Biology mentor, NCERT Biology teacher, Biology diagrams, NEET MCQ practice, Human Physiology mentor, medical entrance Biology faculty",
  },
  {
    id: 2,
    name: "Amit Verma",
    role: "Physics Faculty",
    seoTitle: "JEE Physics Mentor for Mechanics Numericals and Problem Solving",
    category: "JEE",
    subject: "Physics",
    experience: "10+ Years",
    students: "22k+",
    rating: "4.8",
    image:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=900&h=900&fit=crop&auto=format&q=80",
    imageAlt:
      "JEE Physics mentor profile for mechanics numericals and problem solving practice",
    bio: "Helps students master Physics through strong fundamentals, numerical solving methods, formula application, PYQ discussion and JEE-level problem practice.",
    skills: ["Mechanics", "Numericals", "JEE Strategy"],
    keywords:
      "JEE Physics mentor, Physics faculty, Mechanics teacher, JEE numericals, IIT JEE Physics, Physics problem solving, JEE Main mentor",
  },
  {
    id: 3,
    name: "Neha Singh",
    role: "Chemistry Mentor",
    seoTitle: "NEET Chemistry Mentor for Organic Chemistry NCERT and Reactions",
    category: "NEET",
    subject: "Chemistry",
    experience: "8+ Years",
    students: "15k+",
    rating: "4.9",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&h=900&fit=crop&auto=format&q=80",
    imageAlt:
      "Chemistry mentor profile for NEET preparation organic chemistry and NCERT practice",
    bio: "Focuses on Chemistry concepts, reaction understanding, NCERT practice, formula application and regular question solving for NEET and board students.",
    skills: ["Organic Chemistry", "NCERT Practice", "Reaction Basics"],
    keywords:
      "NEET Chemistry mentor, Chemistry teacher, Organic Chemistry, NCERT Chemistry, Chemistry reactions, NEET Chemistry practice, medical entrance Chemistry",
  },
  {
    id: 4,
    name: "Rohit Mishra",
    role: "Foundation Maths Mentor",
    seoTitle: "Foundation Maths Mentor for Class 7 to 10 Concept Building",
    category: "Foundation",
    subject: "Mathematics",
    experience: "7+ Years",
    students: "12k+",
    rating: "4.8",
    image:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=900&h=900&fit=crop&auto=format&q=80",
    imageAlt:
      "Foundation Maths mentor profile for Class 7 to 10 concept building and practice",
    bio: "Builds strong mathematical foundations for junior students through simple explanations, speed practice, worksheets and regular concept-based revision.",
    skills: ["Foundation Maths", "Speed Practice", "Concept Building"],
    keywords:
      "Foundation Maths mentor, Class 7 Maths teacher, Class 8 Maths tuition, Class 9 Maths, Class 10 Maths mentor, Maths concept building",
  },
  {
    id: 5,
    name: "Priya Kapoor",
    role: "Board Exam Specialist",
    seoTitle: "CBSE Board Exam Mentor for Answer Writing and Revision Planning",
    category: "Boards",
    subject: "English & SST",
    experience: "6+ Years",
    students: "9k+",
    rating: "4.7",
    image:
      "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?w=900&h=900&fit=crop&auto=format&q=80",
    imageAlt:
      "CBSE board exam mentor profile for answer writing grammar and revision planning",
    bio: "Guides students in answer writing, paper presentation, grammar, revision planning, sample paper practice and board exam confidence building.",
    skills: ["Answer Writing", "Board Pattern", "Revision Planning"],
    keywords:
      "CBSE board mentor, board exam specialist, answer writing teacher, English grammar mentor, SST mentor, Class 10 board preparation, Class 12 board preparation",
  },
  {
    id: 6,
    name: "Expert Doubt Team",
    role: "Live Doubt Solving Faculty",
    seoTitle: "Live Doubt Solving Faculty for JEE NEET CBSE and NCERT Students",
    category: "Doubt Experts",
    subject: "All Subjects",
    experience: "Daily Support",
    students: "25k+",
    rating: "4.9",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=900&h=900&fit=crop&auto=format&q=80",
    imageAlt:
      "Live doubt solving faculty team for assignments quiz discussion and test analysis",
    bio: "A dedicated mentor team that supports students with live doubts, assignment questions, quiz discussions, chapter revision and test analysis.",
    skills: ["Live Doubts", "Assignments", "Test Discussion"],
    keywords:
      "live doubt solving, doubt experts, JEE doubts, NEET doubts, CBSE doubt solving, NCERT question support, assignment help, test discussion",
  },
];

const mentorStats: MentorStat[] = [
  {
    value: "25+",
    label: "Expert Mentors",
    keywords: "expert mentors, online teachers, subject faculty",
  },
  {
    value: "50k+",
    label: "Students Guided",
    keywords: "students guided, mentor support, online learning guidance",
  },
  {
    value: "4.8★",
    label: "Average Rating",
    keywords: "mentor rating, teacher rating, student feedback",
  },
  {
    value: "Daily",
    label: "Doubt Support",
    keywords: "daily doubt support, live doubt solving, academic support",
  },
];

const supportItems: SupportItem[] = [
  {
    title: "Concept-First Teaching",
    desc: "Teachers explain every topic from basic to advanced level so students understand the logic behind formulas, diagrams, reactions and question patterns.",
    keywords:
      "concept first teaching, concept clarity, basic to advanced learning, teacher explanation",
  },
  {
    title: "Exam-Focused Guidance",
    desc: "Mentor sessions are aligned with school exams, CBSE board patterns, NCERT learning, JEE preparation, NEET preparation and regular test practice.",
    keywords:
      "exam focused guidance, CBSE board guidance, JEE mentor, NEET mentor, NCERT learning",
  },
  {
    title: "Live Doubt Support",
    desc: "Students can ask questions in live sessions and get step-by-step explanations from subject experts and doubt-solving mentors.",
    keywords:
      "live doubt support, step by step explanation, subject experts, doubt solving mentors",
  },
  {
    title: "Performance Feedback",
    desc: "Mentors help students understand test reports, weak topics, revision gaps and the next steps needed for better academic improvement.",
    keywords:
      "performance feedback, test analysis, weak topic tracking, revision planning, score improvement",
  },
];

const teachingFlow: TeachingFlow[] = [
  {
    step: "01",
    title: "Explain",
    desc: "Teachers explain the topic using simple language, examples, diagrams, formulas and visual clarity.",
    keywords:
      "teacher explanation, simple language learning, examples, diagrams, formulas",
  },
  {
    step: "02",
    title: "Practice",
    desc: "Students solve questions, worksheets, DPPs, previous year questions and chapter-based quizzes.",
    keywords:
      "practice worksheets, DPP, PYQ practice, chapter quizzes, question solving",
  },
  {
    step: "03",
    title: "Discuss",
    desc: "Doubts, mistakes, important questions and exam tricks are discussed in class.",
    keywords:
      "doubt discussion, mistake analysis, important questions, exam tricks",
  },
  {
    step: "04",
    title: "Improve",
    desc: "Students get mentor feedback, revision plans and performance guidance based on their test results.",
    keywords:
      "mentor feedback, revision plan, performance guidance, test result analysis",
  },
];

const seoKeywords = [
  "Mediit Academy mentors",
  "online teachers",
  "expert faculty",
  "JEE mentors",
  "NEET mentors",
  "CBSE board mentors",
  "foundation teachers",
  "Class 7 mentors",
  "Class 8 mentors",
  "Class 9 Maths Science mentors",
  "Class 10 board exam mentors",
  "Class 11 JEE NEET mentors",
  "Class 12 board mentors",
  "Physics mentor",
  "Chemistry mentor",
  "Biology mentor",
  "Maths mentor",
  "English mentor",
  "SST mentor",
  "live doubt solving faculty",
  "academic mentors",
  "online tuition teachers",
  "mentor guidance",
  "test analysis",
  "revision planning",
  "performance feedback",
].join(", ");

const getCategoryColor = (category: MentorCategory) => {
  if (category === "JEE") return "bg-blue-50 text-blue-600 border-blue-100";
  if (category === "NEET") return "bg-green-50 text-green-600 border-green-100";
  if (category === "Boards")
    return "bg-orange-50 text-orange-600 border-orange-100";
  if (category === "Foundation")
    return "bg-purple-50 text-purple-600 border-purple-100";
  if (category === "Doubt Experts")
    return "bg-red-50 text-red-600 border-red-100";
  return "bg-slate-50 text-slate-600 border-slate-100";
};

const OurMentors: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<MentorCategory>("All");

  const filteredMentors = useMemo(() => {
    if (activeCategory === "All") return mentors;
    return mentors.filter((mentor) => mentor.category === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    const title =
      activeCategory === "All"
        ? "Our Mentors | Expert Teachers for JEE, NEET, CBSE and Foundation - Mediit Academy"
        : `${activeCategory} Mentors | Expert Teachers and Faculty - Mediit Academy`;

    const description =
      activeCategory === "All"
        ? "Meet Mediit Academy expert teachers and mentors for Foundation, CBSE boards, JEE Main, NEET UG, live doubt solving, revision planning, answer writing and performance improvement."
        : categorySeoText[activeCategory];

    const keywords = [
      seoKeywords,
      categorySeoText[activeCategory],
      `${activeCategory} mentors`,
      `${activeCategory} teachers`,
      `${activeCategory} faculty`,
      `${activeCategory} online coaching mentor`,
    ].join(", ");

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/our-mentors`);
  }, [activeCategory]);

  const mentorSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Mediit Academy Mentors and Teachers",
    description:
      "Expert teachers and mentors for Foundation, CBSE, JEE, NEET and live doubt solving with concept teaching, revision planning and performance feedback.",
    url: "/our-mentors",
    keywords: seoKeywords,
    mainEntity: {
      "@type": "ItemList",
      name: "Expert Teachers and Mentor Profiles",
      itemListElement: mentors.map((mentor, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Person",
          name: mentor.name,
          jobTitle: mentor.role,
          description: mentor.bio,
          knowsAbout: [mentor.subject, ...mentor.skills],
          keywords: mentor.keywords,
          affiliation: {
            "@type": "EducationalOrganization",
            name: "Mediit Academy",
            url: "/",
          },
        },
      })),
    },
  };

  const teachingSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Mediit Academy Teacher and Mentor Teaching Method",
    description:
      "A structured teaching method where mentors explain concepts, guide practice, discuss doubts and help students improve with performance feedback.",
    step: teachingFlow.map((item) => ({
      "@type": "HowToStep",
      name: item.title,
      text: item.desc,
    })),
  };

  const goToGuidance = () => {
    window.location.href = "/talk-to-counselor";
  };

  const scrollToMentors = () => {
    const section = document.getElementById("mentor-list");
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#f7f9fc] font-['Inter'] text-[#101827]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(mentorSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(teachingSchema),
        }}
      />

      <main>
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-white"
          aria-labelledby="our-mentors-heading"
        >
          <div className="absolute -left-24 top-0 h-96 w-96 rounded-full bg-blue-100 blur-3xl" />
          <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-orange-100 blur-3xl" />
          <div className="absolute bottom-0 left-[45%] h-72 w-72 rounded-full bg-purple-100 blur-3xl" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-[#f8fbff] px-4 py-2 shadow-sm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                    T
                  </span>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Teachers and Mentors
                  </p>
                </div>

                <h1
                  id="our-mentors-heading"
                  className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[64px]"
                >
                  Learn from Expert Teachers Who Guide, Support and Improve Every
                  Student
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Mediit Academy mentors help students build concept clarity,
                  exam confidence, doubt-solving habits and better performance
                  through live classes, practice sessions, revision planning,
                  test discussion and personal academic guidance.
                </p>

                <p className="sr-only">{seoKeywords}</p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={scrollToMentors}
                    aria-label="Meet Mediit Academy expert teachers and mentors"
                    className="rounded-2xl bg-[#101827] px-7 py-4 text-sm font-black text-white shadow-xl shadow-slate-900/15 transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    Meet Our Mentors
                  </button>

                  <button
                    type="button"
                    onClick={goToGuidance}
                    aria-label="Talk to academic counselor for mentor guidance"
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black text-slate-950 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-300"
                  >
                    Get Mentor Guidance
                  </button>
                </div>

                <div
                  className="mt-9 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4"
                  aria-label="Mentor statistics"
                >
                  {mentorStats.map((item) => (
                    <article
                      key={item.label}
                      title={item.keywords}
                      className="rounded-3xl border border-slate-100 bg-[#f8fbff] p-4 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-lg"
                    >
                      <p className="text-2xl font-black text-slate-950">
                        {item.value}
                      </p>
                      <p className="mt-1 text-[11px] font-bold text-slate-500">
                        {item.label}
                      </p>
                      <p className="sr-only">{item.keywords}</p>
                    </article>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="relative mx-auto max-w-[590px]">
                  <div className="absolute -left-4 top-14 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Doubts Solved
                    </p>
                    <p className="mt-1 text-2xl font-black text-green-600">
                      1.2L+
                    </p>
                  </div>

                  <div className="absolute -right-4 bottom-16 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Mentor Rating
                    </p>
                    <p className="mt-1 text-2xl font-black text-blue-600">
                      4.8★
                    </p>
                  </div>

                  <div className="rounded-[38px] border border-slate-100 bg-[#101827] p-5 shadow-[0_30px_90px_rgba(15,23,42,0.18)]">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="overflow-hidden rounded-[28px] bg-white">
                        <img
                          src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=900&h=900&fit=crop&auto=format&q=80"
                          alt="NEET Biology mentor profile for NCERT-focused teaching and diagram practice"
                          loading="eager"
                          decoding="async"
                          className="h-[260px] w-full object-cover"
                        />
                        <div className="p-4">
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-600">
                            NEET Biology
                          </p>
                          <h2 className="mt-1 text-xl font-black text-slate-950">
                            NCERT-Focused Teaching
                          </h2>
                          <p className="mt-1 text-xs font-bold text-slate-500">
                            Diagram practice · MCQ revision · concept clarity
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4">
                        <div className="rounded-[28px] bg-white/10 p-5 text-white">
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-200">
                            Mentor Support
                          </p>
                          <h2 className="mt-2 text-2xl font-black leading-tight">
                            Live teaching, doubt help and performance feedback.
                          </h2>
                          <p className="mt-3 text-sm leading-6 text-white/60">
                            Students get guidance beyond classes with test
                            discussion, revision support and regular practice.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="rounded-[24px] bg-white p-4 text-center">
                            <p className="text-2xl font-black text-blue-600">
                              25+
                            </p>
                            <p className="text-xs font-bold text-slate-500">
                              Faculty
                            </p>
                          </div>

                          <div className="rounded-[24px] bg-white p-4 text-center">
                            <p className="text-2xl font-black text-green-600">
                              Daily
                            </p>
                            <p className="text-xs font-bold text-slate-500">
                              Doubts
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-6 left-8 right-8 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl sm:block">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-black text-slate-950">
                          Today&apos;s Mentor Session
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          Physics Doubt Class · 08:00 PM
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={goToGuidance}
                        aria-label="View mentor guidance session"
                        className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-black text-white"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SUPPORT */}
        <section className="bg-[#f7f9fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-9 grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-6">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Mentor Support System
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Teachers Help Students Learn with Clarity, Practice and
                  Confidence
                </h2>
              </div>

              <p className="text-sm leading-6 text-slate-500 lg:col-span-6">
                The mentor role is not limited to teaching. They guide students
                through doubts, practice, test discussion, revision planning and
                performance improvement.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {supportItems.map((item, index) => (
                <article
                  key={item.title}
                  title={item.keywords}
                  className="group rounded-[28px] border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-lg font-black text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>

                  <p className="sr-only">{item.keywords}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* MENTOR LIST */}
        <section id="mentor-list" className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Faculty Profiles
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Meet the Teachers Behind Mediit Learning
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Choose mentors by category and explore subject expertise,
                teaching focus, exam guidance and student support areas.
              </p>
            </div>

            <div
              className="mb-8 flex gap-3 overflow-x-auto pb-2"
              aria-label="Mentor category filters"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  title={categorySeoText[category]}
                  aria-label={`Show ${category} mentors. ${categorySeoText[category]}`}
                  className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-black transition-all duration-300 ${
                    activeCategory === category
                      ? "border-[#101827] bg-[#101827] text-white shadow-lg shadow-slate-900/15"
                      : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <p className="mb-6 text-sm leading-6 text-slate-500">
              {categorySeoText[activeCategory]}
            </p>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredMentors.map((mentor) => (
                <article
                  key={mentor.id}
                  title={mentor.keywords}
                  className="group overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
                >
                  <div className="relative h-[300px] overflow-hidden bg-slate-100">
                    <img
                      src={mentor.image}
                      alt={mentor.imageAlt}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

                    <span
                      className={`absolute left-4 top-4 rounded-full border px-3 py-1 text-xs font-black ${getCategoryColor(
                        mentor.category
                      )}`}
                    >
                      {mentor.category}
                    </span>

                    <span className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black text-slate-950 backdrop-blur">
                      {mentor.rating} ★
                    </span>

                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-200">
                        {mentor.subject}
                      </p>
                      <h3 className="mt-2 text-2xl font-black leading-tight tracking-[-0.03em] text-white">
                        {mentor.name}
                      </h3>
                      <p className="mt-1 text-sm font-bold text-white/70">
                        {mentor.role}
                      </p>
                    </div>
                  </div>

                  <div className="p-5">
                    <p className="text-sm font-bold text-blue-700">
                      {mentor.seoTitle}
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {mentor.bio}
                    </p>

                    <p className="sr-only">{mentor.keywords}</p>

                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl bg-slate-50 p-4">
                        <p className="text-xs font-bold text-slate-500">
                          Experience
                        </p>
                        <p className="mt-1 text-sm font-black text-slate-950">
                          {mentor.experience}
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-4">
                        <p className="text-xs font-bold text-slate-500">
                          Students
                        </p>
                        <p className="mt-1 text-sm font-black text-slate-950">
                          {mentor.students}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {mentor.skills.map((skill) => (
                        <span
                          key={skill}
                          className="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-600"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={goToGuidance}
                      aria-label={`Get guidance from ${mentor.name}, ${mentor.seoTitle}`}
                      className="mt-6 w-full rounded-2xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                    >
                      Get Mentor Guidance
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* TEACHING FLOW */}
        <section className="bg-[#f7f9fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-9 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Teaching Method
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  A Simple Mentor Method That Keeps Students Improving
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Every teacher follows a structured teaching flow so students can
                understand, practice, discuss and improve step by step.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {teachingFlow.map((item) => (
                <article
                  key={item.step}
                  title={item.keywords}
                  className="rounded-[28px] border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101827] text-lg font-black text-white">
                    {item.step}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>

                  <p className="sr-only">{item.keywords}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white py-12">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">
                    Guided by Expert Teachers
                  </p>

                  <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
                    Give Students the Right Mentor Support at Every Step
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    Mediit Academy teachers support students with live teaching,
                    doubt solving, test discussion, revision planning,
                    performance feedback and continuous academic improvement.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={scrollToMentors}
                    aria-label="Meet Mediit Academy teachers and mentors"
                    className="rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                  >
                    Meet Mentors
                  </button>

                  <button
                    type="button"
                    onClick={goToGuidance}
                    aria-label="Talk to counselor for mentor guidance"
                    className="rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-sm font-black text-white backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                  >
                    Talk to Counselor
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OurMentors;